/**
 * PAGE ADMIN — Automatisations BED (migration 00179)
 *
 * Cahier Desertours L30-33 / L35-51 / L53-54 / L59-76 / L93-94 / L123 / L137-151.
 *
 * Trois blocs, pas un de plus :
 *   1. les RÈGLES (événements + relances), réglables inline ;
 *   2. la PRÉVISUALISATION des relances dues, avec le verdict de l'anti-spam
 *      cible par cible — l'admin doit distinguer « sélectionné » de « partira » ;
 *   3. la CLÔTURE d'édition.
 *
 * Le contenu des mails, lui, n'est PAS ici : ce sont des modèles ordinaires de
 * « Modèles d'email », éditables au même endroit que tous les autres.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Play, TestTube2 } from 'lucide-react';
import { PageContent, PageHeader, PageLayout } from '../components/page';
import { BedAutomationRuleRow } from '../components/bed/BedAutomationRuleRow';
import { BedEditionClosureCard } from '../components/bed/BedEditionClosureCard';
import { Badge, Button, ErrorState, LoadingState, toast } from '../components/ui';
import { useEmailTemplates } from '../hooks/useEmailTemplates';
import { ROUTES } from '../lib/routes';
import {
  bedAutomationErrorMessage,
  gateReasonLabel,
  getBedAutomationRules,
  getBedReminderPreview,
  runBedAutomations,
  setBedAutomationRule,
  type BedAutomationRules,
  type BedReminderTarget,
  type BedRulePatch,
} from '../lib/bed/automations';

export function BedAutomationsPage() {
  const { templates } = useEmailTemplates();
  const [data, setData] = useState<BedAutomationRules | null>(null);
  const [targets, setTargets] = useState<BedReminderTarget[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [rules, preview] = await Promise.all([getBedAutomationRules(), getBedReminderPreview()]);

    if (rules.status !== 'ok') {
      setError(bedAutomationErrorMessage(rules.status));
      setLoading(false);
      return;
    }
    setError(null);
    setData(rules.data);
    setTargets(preview.status === 'ok' ? preview.data : []);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function handlePatch(key: string, patch: BedRulePatch) {
    setBusy(true);
    const res = await setBedAutomationRule(key, patch);
    if (res.status === 'ok') {
      setData(res.data);
      toast.success('Règle mise à jour');
      // Le nombre de cibles dues dépend du délai et de l'activation : il faut
      // le recalculer, pas seulement rafraîchir la règle.
      const preview = await getBedReminderPreview();
      if (preview.status === 'ok') setTargets(preview.data);
    } else {
      toast.error(bedAutomationErrorMessage(res.status));
    }
    setBusy(false);
  }

  async function handleRun(mode: 'run' | 'preview') {
    setBusy(true);
    const res = await runBedAutomations(mode);
    if (res.status === 'ok') {
      const { reminders, queue } = res.data;
      toast.success(
        mode === 'preview'
          ? `Simulation : ${reminders?.selected ?? 0} cible(s), ${queue.processed} mail(s) rendus, 0 envoyé`
          : `${reminders?.enqueued ?? 0} relance(s) mise(s) en file · ${queue.sent} envoyé(s), ${queue.failed} en échec`
      );
      await refresh();
    } else {
      toast.error(res.message ?? "L'exécution a échoué.");
    }
    setBusy(false);
  }

  if (loading) {
    return (
      <PageLayout>
        <PageHeader breadcrumbs={[{ label: 'Admin' }, { label: 'Automatisations BED' }]} />
        <div className="h-px w-full bg-zinc-200" />
        <LoadingState message="Chargement des automatisations..." />
      </PageLayout>
    );
  }

  if (error || !data) {
    return (
      <PageLayout>
        <PageHeader breadcrumbs={[{ label: 'Admin' }, { label: 'Automatisations BED' }]} />
        <div className="h-px w-full bg-zinc-200" />
        <ErrorState
          title="Automatisations BED indisponibles"
          message={error ?? 'Une erreur est survenue.'}
          onRetry={refresh}
        />
      </PageLayout>
    );
  }

  const events = data.rules.filter((r) => r.kind === 'event');
  const reminders = data.rules.filter((r) => r.kind === 'reminder');
  const willSend = targets.filter((t) => t.gate.allowed).length;

  return (
    <PageLayout>
      <PageHeader breadcrumbs={[{ label: 'Admin' }, { label: 'Automatisations BED' }]} />
      <div className="h-px w-full bg-zinc-200" />

      <PageContent
        title="Automatisations BED"
        description="Emails automatiques, relances programmables et clôture d'édition du bulletin d'engagement."
        actions={
          <>
            <Button variant="outline" disabled={busy} onClick={() => handleRun('preview')}>
              <TestTube2 className="size-4" aria-hidden />
              Simuler
            </Button>
            <Button disabled={busy} onClick={() => handleRun('run')}>
              <Play className="size-4" aria-hidden />
              Exécuter maintenant
            </Button>
          </>
        }
      >
        <div className="flex flex-col divide-y divide-zinc-200">
          <section className="flex flex-col">
            <div className="flex flex-wrap items-center justify-between gap-2 px-page-padding pt-6 pb-2">
              <div className="flex flex-col gap-1">
                <h2 className="text-base font-semibold text-zinc-900">Emails événementiels</h2>
                <p className="text-sm text-zinc-500">
                  Envoyés au moment où le fait se produit : annulation ou report confirmé,
                  inscription définitive, encaissement du solde.
                </p>
              </div>
              <Link
                to={ROUTES.ADMIN.EMAIL_TEMPLATES}
                className="flex items-center gap-1.5 text-sm text-zinc-600 underline underline-offset-4 hover:text-zinc-900"
              >
                Éditer les modèles d'email
                <ExternalLink className="size-3.5" aria-hidden />
              </Link>
            </div>
            {events.map((rule) => (
              <BedAutomationRuleRow
                key={rule.key}
                rule={rule}
                templates={templates}
                dueNow={0}
                disabled={busy}
                onPatch={handlePatch}
              />
            ))}
          </section>

          <section className="flex flex-col">
            <div className="flex flex-col gap-1 px-page-padding pt-6 pb-2">
              <h2 className="text-base font-semibold text-zinc-900">Relances programmées</h2>
              <p className="text-sm text-zinc-500">
                Évaluées chaque jour à 07h30 UTC. Les équipages annulés, reportés, inscrits
                définitifs ou archivés en sont exclus d'office.
              </p>
            </div>
            {reminders.map((rule) => (
              <BedAutomationRuleRow
                key={rule.key}
                rule={rule}
                templates={templates}
                dueNow={data.due_now[rule.key] ?? 0}
                disabled={busy}
                onPatch={handlePatch}
              />
            ))}
          </section>

          <section className="flex flex-col gap-3 px-page-padding py-6">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-base font-semibold text-zinc-900">Cibles du prochain passage</h2>
              <Badge variant="secondary">{targets.length} sélectionnée(s)</Badge>
              <Badge variant="outline">{willSend} partiraient réellement</Badge>
            </div>

            {targets.length === 0 ? (
              <p className="text-sm text-zinc-500">
                Aucun équipage ne remplit les conditions d'une relance aujourd'hui.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[52rem] text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 text-left text-xs tracking-wide text-zinc-500 uppercase">
                      <th className="py-2 pr-4 font-medium">Équipage</th>
                      <th className="py-2 pr-4 font-medium">Statut BED</th>
                      <th className="py-2 pr-4 font-medium">Règle</th>
                      <th className="py-2 pr-4 font-medium">Motif</th>
                      <th className="py-2 font-medium">Anti-spam</th>
                    </tr>
                  </thead>
                  <tbody>
                    {targets.map((t) => (
                      <tr
                        key={`${t.rule_key}:${t.crew_id}`}
                        className="border-b border-zinc-100 last:border-b-0"
                      >
                        <td className="py-2 pr-4">
                          <Link
                            to={ROUTES.DATA.CREWS.DETAIL(t.crew_id)}
                            className="text-zinc-900 underline underline-offset-4"
                          >
                            {t.crew_number ?? t.crew_id.slice(0, 8)}
                          </Link>
                          <span className="ml-2 text-zinc-500">{t.crew_name}</span>
                        </td>
                        <td className="py-2 pr-4 text-zinc-600">{t.bed_status_label}</td>
                        <td className="py-2 pr-4 text-zinc-600">{t.rule_key}</td>
                        <td className="py-2 pr-4 text-zinc-600">{t.detail}</td>
                        <td className="py-2">
                          <Badge variant={t.gate.allowed ? 'secondary' : 'outline'}>
                            {gateReasonLabel(t.gate)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <BedEditionClosureCard />
        </div>
      </PageContent>
    </PageLayout>
  );
}
